/**
 * "Gallery" — designer Cover 5.
 * Solid near-black masthead panel holding all the type (photo stays fully
 * visible below it), hairline rule, meta row with compact serif stat
 * numbers, and carousel progress dots + "ЕЩЁ ❯❯" cue over the bottom scrim.
 *
 * The panel height is computed from the content (dry-run layout first,
 * then paint), so cleared texts shrink it and long content never spills
 * past its edge. Stats that don't fit beside the subtitle drop to a
 * second row inside a taller panel.
 */

import { SLIDE_W, SLIDE_H, type CarouselTemplate, type CoverSlideData, type SlideStyle } from "../types";
import {
  drawDesignerBackdrop, drawMockScrim, drawWatermark,
  drawTrackedText, drawTrackedRight, measureTracked,
  splitStatValue, drawChevron, drawSerifHeadline,
} from "../shared";
import { NUMBER_FONT } from "../fonts";
import { fieldDiary } from "./field-diary";

function roundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, r);
  ctx.fill();
}

function drawCover(ctx: CanvasRenderingContext2D, data: CoverSlideData, style: SlideStyle): void {
  const W = SLIDE_W, H = SLIDE_H, PAD = 64;
  const DISPLAY = style.fonts.display;
  const BODY = style.fonts.body;

  const kicker = data.kickerText.trim();
  const subtitle = data.subtitle.trim();
  const filledStats = data.stats.filter((s) => s.value.trim() !== "");
  const ks = data.kickerScale ?? 1;
  const ss = data.subtitleScale ?? 1;
  const st = data.statsScale ?? 1;

  // ── Dry-run layout: everything that lives on the panel ──
  const kickerSize = 34 * ks;
  const kickerBase = 58 + kickerSize;
  const capTop = kicker ? kickerBase + 49 : 80;
  const size = 250 * (data.headlineFontSize / 140);
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  const lastBaseline = drawSerifHeadline(ctx, data.headlineTitle, PAD, 0, W - PAD * 2, size, DISPLAY, {
    capTop,
    dryRun: true,
  });
  const ruleY = lastBaseline + 22;

  // Subtitle metrics
  ctx.font = `400 ${40 * ss}px ${BODY}`;
  const subtitleBase = ruleY + 83 * Math.max(ss, 0.8);
  const subtitleEnd = subtitle ? PAD + measureTracked(ctx, subtitle.toUpperCase(), 10 * ss) : PAD;

  // Stat segment metrics (right-aligned block)
  interface Seg { num: string | null; label: string; text: string; segW: number; numW: number }
  const segs: Seg[] = filledStats.map((s) => {
    const { num, label } = splitStatValue(s.value);
    const text = s.value.trim().toUpperCase();
    let numW = 0;
    if (num) {
      ctx.font = `400 ${42 * st}px ${NUMBER_FONT}`;
      numW = ctx.measureText(num).width;
    } else {
      ctx.font = `400 ${32 * st}px ${DISPLAY}`;
      numW = ctx.measureText(text).width;
    }
    ctx.font = `500 ${20 * st}px ${BODY}`;
    const labelW = num ? measureTracked(ctx, label.toUpperCase(), 4 * st) : 0;
    return { num, label, text, segW: Math.max(numW, labelW), numW };
  });
  const SEG_GAP = 46;
  const statsTotal = segs.reduce((a, s) => a + s.segW, 0) + Math.max(0, segs.length - 1) * SEG_GAP;

  const NUM_BASE = ruleY + 77;
  const LABEL_BASE = NUM_BASE + 30 * st;
  const sameRow = !subtitle || W - PAD - statsTotal >= subtitleEnd + 30;
  const statsNumBase = sameRow ? NUM_BASE : LABEL_BASE + 52;
  const statsLabelBase = statsNumBase + 30 * st;

  // Panel bottom: below the deepest drawn element
  let contentBottom = lastBaseline;
  if (subtitle || segs.length > 0) contentBottom = Math.max(contentBottom, ruleY);
  if (subtitle) contentBottom = Math.max(contentBottom, subtitleBase);
  if (segs.length > 0) contentBottom = Math.max(contentBottom, segs.some((s) => s.num) ? statsLabelBase : statsNumBase);
  const panelH = contentBottom + 52;

  // ── Paint ──
  ctx.clearRect(0, 0, W, H);
  const textOnLight = drawDesignerBackdrop(ctx, data);
  ctx.fillStyle = textOnLight ? "rgba(243,246,243,0.94)" : "rgba(5,13,10,0.94)";
  ctx.fillRect(0, 0, W, panelH);
  drawMockScrim(ctx, 300, false, !textOnLight);

  const rgb = textOnLight ? "28,36,32" : "255,255,255";
  const solid = textOnLight ? "#1c2420" : "#ffffff";

  if (kicker) {
    ctx.font = `500 ${kickerSize}px ${BODY}`;
    ctx.fillStyle = `rgba(${rgb},0.48)`;
    drawTrackedText(ctx, kicker.toUpperCase(), PAD, kickerBase, 10 * ks);
  }

  ctx.fillStyle = solid;
  drawSerifHeadline(ctx, data.headlineTitle, PAD, 0, W - PAD * 2, size, DISPLAY, { capTop });

  if (subtitle || segs.length > 0) {
    ctx.fillStyle = `rgba(${rgb},0.14)`;
    ctx.fillRect(PAD, ruleY, W - PAD * 2, 1);
  }

  if (subtitle) {
    ctx.font = `400 ${40 * ss}px ${BODY}`;
    ctx.fillStyle = `rgba(${rgb},0.7)`;
    drawTrackedText(ctx, subtitle.toUpperCase(), PAD, subtitleBase, 10 * ss);
  }

  // Right-aligned stat segments with hairline dividers
  let x = W - PAD - statsTotal;
  segs.forEach((seg, i) => {
    if (i > 0) {
      ctx.fillStyle = `rgba(${rgb},0.16)`;
      ctx.fillRect(x - SEG_GAP / 2, statsNumBase - 33 * st, 1, 48 * st);
    }
    if (seg.num) {
      ctx.font = `400 ${42 * st}px ${NUMBER_FONT}`;
      ctx.fillStyle = solid;
      ctx.fillText(seg.num, x, statsNumBase);
      if (seg.label) {
        ctx.font = `500 ${20 * st}px ${BODY}`;
        ctx.fillStyle = `rgba(${rgb},0.4)`;
        drawTrackedText(ctx, seg.label.toUpperCase(), x, statsLabelBase, 4 * st);
      }
    } else {
      ctx.font = `400 ${32 * st}px ${DISPLAY}`;
      ctx.fillStyle = solid;
      ctx.fillText(seg.text, x, statsNumBase);
    }
    x += seg.segW + SEG_GAP;
  });

  // ── Progress dots, bottom-left ──
  ctx.fillStyle = solid;
  roundedRect(ctx, PAD, 1250, 36, 8, 4);
  ctx.fillStyle = `rgba(${rgb},0.32)`;
  for (let i = 0; i < 4; i++) roundedRect(ctx, PAD + 48 + i * 20, 1250, 8, 8, 4);

  // ── Swipe cue + "❯❯", bottom-right ──
  ctx.font = `500 26px ${BODY}`;
  ctx.fillStyle = `rgba(${rgb},0.4)`;
  drawTrackedRight(ctx, (data.swipeText?.trim() || "ЕЩЁ").toUpperCase(), 935, 1263, 8);
  ctx.strokeStyle = textOnLight ? "rgba(28,36,32,0.72)" : "rgba(255,255,255,0.72)";
  drawChevron(ctx, 967, 1259, 8, 17, 7);
  ctx.strokeStyle = `rgba(${rgb},0.32)`;
  drawChevron(ctx, 1003, 1259, 8, 17, 7);

  drawWatermark(ctx, style, textOnLight);
}

export const gallery: CarouselTemplate = {
  id: "gallery",
  name: "Gallery",
  premium: false,
  drawCover,
  drawSpecies: fieldDiary.drawSpecies,
  drawClosing: fieldDiary.drawClosing,
};
